import { useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, Typography,
} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { AssetCategory, AssetCriticality, AssetStatus, Site } from '@cmms/types'

import { primary } from '../../../theme/palette'
import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'

// Pattern follows apps/web/src/features/locations/components/ImportDialog.tsx
// — same UX (template download, dropzone, validation, per-row import,
// results) so users see one consistent import flow across the app.

const VALID_STATUSES: AssetStatus[] = ['active', 'inactive', 'under_maintenance', 'decommissioned']
const VALID_CRITICALITIES: AssetCriticality[] = ['critical', 'important', 'normal']

const SAMPLE_CSV = `name,asset_code,description,site,location,category,status,criticality,serial_number,model,manufacturer,purchase_date,purchase_cost,warranty_expiry,notes
AHU — Admin Block,,Air handling unit for the admin block,Greenfield Tech Park,AHU Room,HVAC,active,critical,SN-AHU-001,CGAM-AHU,Trane,2024-03-15,18500,2027-03-15,Quarterly filter swap required
Chiller #1,AST-CHL-001,Main building chiller,Greenfield Tech Park,Mechanical Room,HVAC,active,important,CH-12345,RTHD-200,Trane,2023-08-01,42000,2028-08-01,
Booster Pump,,,Greenfield Tech Park,Pump Room,Plumbing,active,normal,,,,,,,Backup unit only`

interface ImportError {
  row: number
  field: string
  value: string
  message: string
}

interface ParsedRow {
  rowNum: number
  name: string
  asset_code?: string
  description?: string
  site?: string
  location?: string
  category?: string
  status?: AssetStatus
  criticality?: AssetCriticality
  serial_number?: string
  model?: string
  manufacturer?: string
  purchase_date?: string
  purchase_cost?: number
  warranty_expiry?: string
  notes?: string
}

interface Props {
  open: boolean
  onClose: () => void
  sites: Site[]
  categories: AssetCategory[]
}

export function AssetsImportDialog({ open, onClose, sites, categories }: Props) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  const [file, setFile]         = useState<File | null>(null)
  const [parsed, setParsed]     = useState<ParsedRow[] | null>(null)
  const [errors, setErrors]     = useState<ImportError[]>([])
  const [validating, setValidating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [result, setResult]     = useState<{ success: number; failed: number } | null>(null)

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'assets_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setResult(null)
    setErrors([])
    setParsed(null)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const text = ev.target?.result as string
      const { rows, errors: parseErrors } = parseCSV(text, {
        sites:      sites.map((s) => s.name),
        categories: categories.map((c) => c.name),
      })

      // Show the local-validation results immediately so the user isn't
      // staring at a blank panel while we fetch locations.
      setParsed(rows)
      setErrors(parseErrors)

      // Locations need a per-site fetch; if any surviving row references
      // a location, resolve them now and prune the bad ones before we
      // claim "ready to import".
      const needsLocationLookup = rows.some((r) => !!r.location)
      if (!needsLocationLookup) return

      setValidating(true)
      const siteByName = new Map(sites.map((s) => [s.name.toLowerCase(), s]))
      const locationsBySite = new Map<string, { id: string; name: string }[]>()
      const locationErrors: ImportError[] = []
      const survivors: ParsedRow[] = []

      for (const row of rows) {
        if (!row.location) { survivors.push(row); continue }

        // Site existence was already validated in parseCSV; if the row
        // is here, the site name resolves. But re-check to keep TS happy.
        const site = row.site ? siteByName.get(row.site.toLowerCase()) : null
        if (!site) {
          locationErrors.push({ row: row.rowNum, field: 'location', value: row.location,
            message: 'A site must be specified to look up a location.' })
          continue
        }
        let locs = locationsBySite.get(site.id)
        if (!locs) {
          locs = await api.locations.list(site.id).catch(() => [])
          locs = Array.isArray(locs) ? locs : []
          locationsBySite.set(site.id, locs)
        }
        const match = locs.find((l) => l.name.toLowerCase() === row.location!.toLowerCase())
        if (!match) {
          const available = locs.length === 0 ? '(none defined yet)'
            : locs.map((l) => l.name).slice(0, 8).join(', ') + (locs.length > 8 ? ', …' : '')
          locationErrors.push({ row: row.rowNum, field: 'location', value: row.location,
            message: `Location "${row.location}" not found under site "${row.site}". Available: ${available}` })
          continue
        }
        survivors.push(row)
      }

      setParsed(survivors)
      if (locationErrors.length > 0) {
        setErrors((prev) => [...prev, ...locationErrors])
      }
      setValidating(false)
    }
    reader.readAsText(f)
  }

  async function handleImport() {
    if (!parsed || parsed.length === 0) return
    setImporting(true)

    // Pre-index sites + categories by lowercased name for O(1) lookup.
    const siteByName     = new Map(sites.map((s) => [s.name.toLowerCase(), s]))
    const categoryByName = new Map(categories.map((c) => [c.name.toLowerCase(), c]))

    // Locations need a per-site lookup; cache so we don't refetch when
    // many rows share a site. Fetched lazily, only when a row needs it.
    const locationsBySite = new Map<string, { id: string; name: string }[]>()
    async function locationsFor(siteId: string) {
      if (locationsBySite.has(siteId)) return locationsBySite.get(siteId)!
      const list = await api.locations.list(siteId).catch(() => [])
      const arr  = Array.isArray(list) ? list : []
      locationsBySite.set(siteId, arr)
      return arr
    }

    const runErrors: ImportError[] = []
    let success = 0
    let failed  = 0

    for (const row of parsed) {
      try {
        let site_id: string | undefined
        if (row.site) {
          const match = siteByName.get(row.site.toLowerCase())
          if (!match) {
            runErrors.push({ row: row.rowNum, field: 'site', value: row.site,
              message: `Site "${row.site}" doesn't exist. Create it first or check spelling.` })
            failed++
            continue
          }
          site_id = match.id
        }

        let location_id: string | undefined
        if (row.location) {
          if (!site_id) {
            runErrors.push({ row: row.rowNum, field: 'location', value: row.location,
              message: 'A site must be specified to look up a location.' })
            failed++
            continue
          }
          const locs  = await locationsFor(site_id)
          const match = locs.find((l) => l.name.toLowerCase() === row.location!.toLowerCase())
          if (!match) {
            runErrors.push({ row: row.rowNum, field: 'location', value: row.location,
              message: `Location "${row.location}" not found under site "${row.site}".` })
            failed++
            continue
          }
          location_id = match.id
        }

        let category_id: string | undefined
        if (row.category) {
          const match = categoryByName.get(row.category.toLowerCase())
          if (!match) {
            runErrors.push({ row: row.rowNum, field: 'category', value: row.category,
              message: `Category "${row.category}" doesn't exist. Create it first or check spelling.` })
            failed++
            continue
          }
          category_id = match.id
        }

        await api.assets.create({
          name: row.name,
          asset_code:      row.asset_code || undefined,
          description:     row.description || undefined,
          site_id,
          location_id,
          category_id,
          status:          row.status ?? undefined,
          criticality:     row.criticality ?? undefined,
          serial_number:   row.serial_number || undefined,
          model:           row.model || undefined,
          manufacturer:    row.manufacturer || undefined,
          purchase_date:   row.purchase_date || undefined,
          purchase_cost:   row.purchase_cost,
          warranty_expiry: row.warranty_expiry || undefined,
          notes:           row.notes || undefined,
        })
        success++
      } catch (e) {
        failed++
        const msg = e instanceof ApiError ? e.message : 'Unknown error'
        runErrors.push({ row: row.rowNum, field: 'api', value: row.name, message: msg })
      }
    }

    setErrors((prev) => [...prev, ...runErrors])
    setResult({ success, failed })
    setImporting(false)
    if (success > 0) {
      qc.invalidateQueries({ queryKey: ['assets'] })
      toast.success(`${success} asset${success === 1 ? '' : 's'} imported`)
    }
  }

  function reset() {
    setFile(null)
    setParsed(null)
    setErrors([])
    setResult(null)
    setValidating(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleClose() {
    reset()
    onClose()
  }

  const hasValidationErrors = errors.length > 0 && !result
  const allValid = !validating && parsed && parsed.length > 0 && errors.length === 0 && !result

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Import assets</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
          Upload a CSV file to create multiple assets at once. Download the template to see the expected
          format — sites, locations, and categories are matched by name (case-insensitive) and must
          already exist.
        </Typography>

        {/* Download template */}
        <Box
          onClick={downloadSample}
          sx={{
            px: 2.5, py: 2, mb: 2.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider', cursor: 'pointer',
            transition: 'border-color 120ms ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Download template CSV</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                15 columns covering all asset fields
              </Typography>
            </Box>
            <Chip label=".csv" size="small"
              sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Upload
          </Typography>
        </Divider>

        <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />

        {!file ? (
          <Box
            onClick={() => fileRef.current?.click()}
            sx={{
              py: 5, textAlign: 'center', borderRadius: 2,
              border: '2px dashed', borderColor: 'divider', cursor: 'pointer',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: 'primary.main', bgcolor: primary.hover },
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Click to upload CSV</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>or drag and drop</Typography>
          </Box>
        ) : (
          <Box>
            {/* File info */}
            <Stack direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{file.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  {validating
                    ? 'Validating locations…'
                    : parsed
                      ? `${parsed.length} row${parsed.length === 1 ? '' : 's'} ready`
                      : 'Parsing…'}
                </Typography>
              </Box>
              {validating && <CircularProgress size={14} sx={{ color: 'text.secondary' }} />}
              <Button size="small" onClick={reset} disabled={validating}
                sx={{ fontSize: 12, color: 'text.secondary' }}>Change</Button>
            </Stack>

            {hasValidationErrors && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                    {errors.length} issue{errors.length === 1 ? '' : 's'} found
                    {parsed && parsed.length - errors.length > 0
                      ? ` — ${parsed.length - errors.length} row${parsed.length - errors.length === 1 ? '' : 's'} still importable`
                      : ' — fix before importing'}
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', borderRadius: 1.5,
                  border: '1px solid', borderColor: 'rgba(220,38,38,0.2)',
                  bgcolor: 'rgba(220,38,38,0.04)' }}>
                  {errors.map((err, i) => (
                    <Stack key={i} direction="row" spacing={1.5}
                      sx={{ px: 2, py: 1.25,
                        borderBottom: i < errors.length - 1 ? '1px solid' : 'none',
                        borderColor: 'rgba(220,38,38,0.12)' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#DC2626',
                        fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        Row {err.row}
                      </Typography>
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                          {err.field}: <Typography component="span"
                            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>"{err.value}"</Typography>
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{err.message}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}

            {allValid && (
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{ px: 2, py: 1.5, borderRadius: 1.5,
                  bgcolor: 'rgba(5,150,105,0.06)',
                  border: '1px solid', borderColor: 'rgba(5,150,105,0.2)', mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                  {parsed.length} asset{parsed.length === 1 ? '' : 's'} ready to import
                </Typography>
              </Stack>
            )}

            {result && (
              <Box sx={{ mb: 2 }}>
                <Stack spacing={1}>
                  {result.success > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1}
                      sx={{ px: 2, py: 1, borderRadius: 1.5,
                        bgcolor: 'rgba(5,150,105,0.06)',
                        border: '1px solid', borderColor: 'rgba(5,150,105,0.2)' }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                        {result.success} imported successfully
                      </Typography>
                    </Stack>
                  )}
                  {result.failed > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1}
                      sx={{ px: 2, py: 1, borderRadius: 1.5,
                        bgcolor: 'rgba(220,38,38,0.06)',
                        border: '1px solid', borderColor: 'rgba(220,38,38,0.2)' }}>
                      <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                        {result.failed} failed — see errors above
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Format guide */}
        <Box sx={{ mt: 2.5, px: 2.5, py: 2, borderRadius: 2,
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
          border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
            CSV columns
          </Typography>
          <Stack spacing={0.75}>
            <FormatRow field="name" required desc="Asset name" />
            <FormatRow field="asset_code" desc="Optional; auto-generated (AST-00042) when blank" />
            <FormatRow field="description" desc="Free text" />
            <FormatRow field="site" desc="Site name — must already exist" />
            <FormatRow field="location" desc="Location name within the site" />
            <FormatRow field="category" desc="Category name — must already exist" />
            <FormatRow field="status" desc={VALID_STATUSES.join(' | ')} />
            <FormatRow field="criticality" desc={VALID_CRITICALITIES.join(' | ')} />
            <FormatRow field="serial_number, model, manufacturer" desc="Free text" />
            <FormatRow field="purchase_date, warranty_expiry" desc="YYYY-MM-DD" />
            <FormatRow field="purchase_cost" desc="Number (no currency symbol)" />
            <FormatRow field="notes" desc="Free text" />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} sx={{ fontSize: 13, color: 'text.secondary' }}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button variant="contained"
            onClick={handleImport}
            disabled={!allValid || importing || validating}
            startIcon={(importing || validating) ? <CircularProgress size={14} /> : <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: 13, fontWeight: 600 }}>
            {validating
              ? 'Validating…'
              : importing
                ? 'Importing…'
                : `Import ${parsed?.length ?? 0} asset${(parsed?.length ?? 0) === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

function FormatRow({ field, required, desc }: { field: string; required?: boolean; desc: string }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
        color: 'text.primary', minWidth: 200, whiteSpace: 'nowrap' }}>
        {field}{required && <Typography component="span" sx={{ color: '#DC2626' }}>*</Typography>}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{desc}</Typography>
    </Stack>
  )
}

// ─── CSV parser with validation ─────────────────────────────────────────────
//
// Validates locally so users see all problems in one pass before any
// network call. Name resolution (site/location/category) is deferred to
// handleImport because it needs async lookups (locations per site).

function parseCSV(
  text: string,
  ctx: { sites: string[]; categories: string[] }
): { rows: ParsedRow[]; errors: ImportError[] } {
  const lines = text.replace(/\r\n/g, '\n').trim().split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length < 2) {
    return { rows: [], errors: [{ row: 1, field: 'file', value: '', message: 'CSV must have a header row and at least one data row.' }] }
  }

  const header = lines[0]!.split(',').map((h) =>
    h.trim().toLowerCase().replace(/^"/, '').replace(/"$/, '')
  )
  const idx = (col: string) => header.indexOf(col)
  const nameIdx = idx('name')

  const errors: ImportError[] = []
  if (nameIdx < 0) {
    errors.push({ row: 1, field: 'header', value: header.join(', '), message: 'Missing required column "name".' })
    return { rows: [], errors }
  }

  const codeIdx       = idx('asset_code')
  const descIdx       = idx('description')
  const siteIdx       = idx('site')
  const locIdx        = idx('location')
  const catIdx        = idx('category')
  const statusIdx     = idx('status')
  const critIdx       = idx('criticality')
  const serialIdx     = idx('serial_number')
  const modelIdx      = idx('model')
  const mfgIdx        = idx('manufacturer')
  const pdateIdx      = idx('purchase_date')
  const pcostIdx      = idx('purchase_cost')
  const warrantyIdx   = idx('warranty_expiry')
  const notesIdx      = idx('notes')

  const sitesLower      = new Set(ctx.sites.map((s) => s.toLowerCase()))
  const categoriesLower = new Set(ctx.categories.map((c) => c.toLowerCase()))

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols  = parseCSVLine(lines[i]!)
    const get   = (j: number) => (j < 0 ? '' : (cols[j] ?? '').trim())
    const rowNum = i + 1

    const name = get(nameIdx)
    if (!name) {
      errors.push({ row: rowNum, field: 'name', value: '', message: 'Name is required.' })
      continue
    }

    const status = get(statusIdx).toLowerCase()
    if (status && !VALID_STATUSES.includes(status as AssetStatus)) {
      errors.push({ row: rowNum, field: 'status', value: status,
        message: `Invalid status. Must be one of: ${VALID_STATUSES.join(', ')}` })
      continue
    }
    const criticality = get(critIdx).toLowerCase()
    if (criticality && !VALID_CRITICALITIES.includes(criticality as AssetCriticality)) {
      errors.push({ row: rowNum, field: 'criticality', value: criticality,
        message: `Invalid criticality. Must be one of: ${VALID_CRITICALITIES.join(', ')}` })
      continue
    }

    const site = get(siteIdx)
    if (site && !sitesLower.has(site.toLowerCase())) {
      errors.push({ row: rowNum, field: 'site', value: site,
        message: `Site "${site}" doesn't exist in this workspace. Available: ${ctx.sites.join(', ') || '(none)'}` })
      continue
    }

    const category = get(catIdx)
    if (category && !categoriesLower.has(category.toLowerCase())) {
      errors.push({ row: rowNum, field: 'category', value: category,
        message: `Category "${category}" doesn't exist. Available: ${ctx.categories.join(', ') || '(none)'}` })
      continue
    }

    const purchaseDate = get(pdateIdx)
    if (purchaseDate && !isIsoDate(purchaseDate)) {
      errors.push({ row: rowNum, field: 'purchase_date', value: purchaseDate,
        message: 'Date must be in YYYY-MM-DD format.' })
      continue
    }
    const warrantyExpiry = get(warrantyIdx)
    if (warrantyExpiry && !isIsoDate(warrantyExpiry)) {
      errors.push({ row: rowNum, field: 'warranty_expiry', value: warrantyExpiry,
        message: 'Date must be in YYYY-MM-DD format.' })
      continue
    }

    const purchaseCostRaw = get(pcostIdx)
    let purchaseCost: number | undefined
    if (purchaseCostRaw) {
      const parsed = Number(purchaseCostRaw.replace(/,/g, ''))
      if (Number.isNaN(parsed) || parsed < 0) {
        errors.push({ row: rowNum, field: 'purchase_cost', value: purchaseCostRaw,
          message: 'Purchase cost must be a non-negative number (no currency symbol).' })
        continue
      }
      purchaseCost = parsed
    }

    rows.push({
      rowNum,
      name,
      asset_code:      get(codeIdx) || undefined,
      description:     get(descIdx) || undefined,
      site:            site || undefined,
      location:        get(locIdx) || undefined,
      category:        category || undefined,
      status:          (status || undefined) as AssetStatus | undefined,
      criticality:     (criticality || undefined) as AssetCriticality | undefined,
      serial_number:   get(serialIdx) || undefined,
      model:           get(modelIdx) || undefined,
      manufacturer:    get(mfgIdx) || undefined,
      purchase_date:   purchaseDate || undefined,
      purchase_cost:   purchaseCost,
      warranty_expiry: warrantyExpiry || undefined,
      notes:           get(notesIdx) || undefined,
    })
  }

  return { rows, errors }
}

function isIsoDate(s: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
  const d = new Date(s)
  return !Number.isNaN(d.getTime())
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      // Handle escaped quotes: "" inside a quoted cell → literal "
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; continue }
      inQuotes = !inQuotes
      continue
    }
    if (ch === ',' && !inQuotes) { result.push(current); current = ''; continue }
    current += ch
  }
  result.push(current)
  return result
}
